import { ShoppingCart, User, Menu, X, Bell, Book, Tag, UserCircle, Settings, LogOut, Heart, Package, Loader2, Trash2, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { getImageUrl, handleImageError } from '../utils/imageUtils';
import SearchBar from './SearchBar';
import axios from 'axios';

interface UserProfile {
  name: string;
  email: string;
  role: string;
}

interface Notification {
  id: number;
  user_id: number;
  order_id: number;
  message: string;
  status: string;
  read: boolean;
  created_at: string;
}

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const { cartItems, cartItemCount, cartTotal, updateCartItemQuantity, removeFromCart } = useCart();
  const [updatingItem, setUpdatingItem] = useState<number | null>(null);
  const [removingItem, setRemovingItem] = useState<number | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  const isAdmin = userProfile?.role === 'admin';

  const searchRef = useRef<HTMLDivElement>(null);
  const cartRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Navigation items with active state detection
  const navItems = useMemo(() => [
    { path: '/books', label: 'الكتب', icon: Book },
    { path: '/categories', label: 'التّصنيفات', icon: Tag },
    { path: '/authors', label: 'الكتّاب', icon: UserCircle },
    //{ path: '/new-releases', label: 'الإصدارات الجديدة', icon: Star },
    //{ path: '/trending', label: 'الأكثر مبيعاً', icon: TrendingUp }
  ], []);

  const isActiveLink = useCallback((path: string) => {
    return location.pathname === path;
  }, [location.pathname]);

  // Fetch user notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingNotifications(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'success') {
        setNotifications(response.data.data);
        setNotificationCount(response.data.results);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, [isAuthenticated]);

  // Helper function to get time ago in Arabic
  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'منذ لحظات';
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `منذ ${minutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `منذ ${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`;
    }
    const days = Math.floor(diffInSeconds / 86400);
    return `منذ ${days} ${days === 1 ? 'يوم' : 'أيام'}`;
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${import.meta.env.VITE_API_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data.user) {
          setUserProfile(response.data.user);
        }
      } catch (error) {
        console.error('خطأ في جلب ملف المستخدم:', error);
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchUserProfile();
      // Fetch notifications when authenticated
      fetchNotifications();
      
      // Set up interval to check for notifications every 2 minutes
      const interval = setInterval(fetchNotifications, 2 * 60 * 1000);
      return () => clearInterval(interval);
    } else {
      setUserProfile(null);
      setNotifications([]);
      setNotificationCount(0);
      setLoading(false);
    }
  }, [isAuthenticated, logout, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
      if (cartRef.current && !cartRef.current.contains(event.target as Node)) {
        setIsCartOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdowns when a link is clicked
  const closeAllDropdowns = useCallback(() => {
    setIsSearchOpen(false);
    setIsCartOpen(false);
    setIsUserMenuOpen(false);
    setIsNotificationsOpen(false);
    setIsMenuOpen(false);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    closeAllDropdowns();
  }, [location.pathname, closeAllDropdowns]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
      }
      if (event.key === 'Escape') {
        setIsSearchOpen(false);
        setIsCartOpen(false);
        setIsUserMenuOpen(false);
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    closeAllDropdowns();
    navigate('/login');
  }, [logout, navigate, closeAllDropdowns]);

  const toggleMenu = useCallback(() => setIsMenuOpen(!isMenuOpen), [isMenuOpen]);
  const toggleSearch = useCallback(() => setIsSearchOpen(!isSearchOpen), [isSearchOpen]);
  const toggleCart = useCallback(() => setIsCartOpen(!isCartOpen), [isCartOpen]);
  const toggleUserMenu = useCallback(() => setIsUserMenuOpen(!isUserMenuOpen), [isUserMenuOpen]);
  const toggleNotifications = useCallback(() => setIsNotificationsOpen(!isNotificationsOpen), [isNotificationsOpen]);

  const handleUpdateQuantity = async (bookId: number, currentQuantity: number, increment: boolean) => {
    const newQuantity = increment ? currentQuantity + 1 : currentQuantity - 1;
    
    if (newQuantity < 1) return;
    
    setUpdatingItem(bookId);
    try {
      await updateCartItemQuantity(bookId, newQuantity);
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      setUpdatingItem(null);
    }
  };

  const handleRemoveItem = async (bookId: number) => {
    setRemovingItem(bookId);
    try {
      await removeFromCart(bookId);
    } catch (error) {
      console.error('Error removing item:', error);
    } finally {
      setRemovingItem(null);
    }
  };
  
  // Function to mark notification as read and navigate to order
  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark as read on the server
      const token = localStorage.getItem('token');
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/notifications/${notification.id}/read`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      setNotificationCount(prev => prev - 1);
      
      // Navigate to the order
      navigate(`/orders`);
      closeAllDropdowns();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Function to mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/notifications/read-all`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Clear notifications from local state
      setNotifications([]);
      setNotificationCount(0);
      
      // Navigate to orders page
      navigate('/orders');
      closeAllDropdowns();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Get appropriate icon based on notification status
  const getNotificationIcon = (status: string) => {
    switch(status) {
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'processing': return <Package className="w-4 h-4 text-blue-500" />;
      case 'shipped': return <Package className="w-4 h-4 text-indigo-500" />;
      case 'delivered': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 sticky top-0 z-50 transition-all duration-200" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Navigation */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2 hover:scale-105 transition-transform duration-200">
              <picture>
                {/* WebP format for modern browsers - smallest file size */}
                <source 
                  srcSet="/logo-64.webp 1x, /logo-128.webp 2x" 
                  type="image/webp"
                />
                {/* PNG fallback for older browsers */}
                <source 
                  srcSet="/logo-64.png 1x, /logo-128.png 2x" 
                  type="image/png"
                />
                {/* Default fallback */}
                <img
                  src="/logo-64.png"
                  alt="Logo"
                  className="w-16 h-16 rounded-lg object-contain"
                  width="64"
                  height="64"
                  loading="eager"
                />
              </picture>
            </Link>
            
            <nav className="hidden lg:flex gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActiveLink(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:bg-orange-50 hover:text-orange-600 ${
                      isActive 
                        ? 'bg-gradient-to-r from-orange-50 to-amber-50 text-orange-600 shadow-sm' 
                        : 'text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          
          {/* Search, Actions, and User */}
          <div className="flex items-center gap-3">
            {/* Using the new SearchBar component with forwarded ref */}
            <SearchBar 
              ref={searchRef}
              isOpen={isSearchOpen} 
              toggleSearch={toggleSearch} 
              closeAllDropdowns={closeAllDropdowns} 
            />

            {/* Enhanced Notifications */}
            <div className="relative" ref={notificationsRef}>
              <button 
                onClick={toggleNotifications}
                className="p-2.5 hover:bg-gray-100 rounded-lg transition-all duration-200 relative hover:scale-105"
                aria-label="الإشعارات"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                    {notificationCount}
                  </span>
                )}
              </button>

              {isNotificationsOpen && (
                <div
                  className="
                    fixed inset-x-0 top-16 mx-2 z-50 max-w-sm w-full rounded-xl shadow-xl border border-gray-200 bg-white
                    md:absolute md:inset-x-auto md:top-full md:left-0 md:mt-2 md:w-80
                  "
                  style={{ maxHeight: '70vh', overflowY: 'auto' }}
                >
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">الإشعارات</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto scrollbar-hide">
                    {loadingNotifications ? (
                      <div className="flex justify-center items-center p-6">
                        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p>لا توجد إشعارات جديدة</p>
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div 
                          key={notification.id} 
                          className="p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start">
                            <div className="ml-3 mt-1">
                              {getNotificationIcon(notification.status)}
                            </div>
                            <div>
                              <p className="text-sm text-gray-900 mb-1">{notification.message}</p>
                              <p className="text-xs text-gray-500">{getTimeAgo(notification.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="p-4 border-t border-gray-100">
                      <div className="flex flex-col gap-2">
                        <button 
                          className="w-full text-center text-sm text-orange-500 hover:text-orange-600 font-medium transition-colors"
                          onClick={markAllAsRead}
                        >
                          تحديد الكل كمقروء
                        </button>
                        <button 
                          className="w-full text-center text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
                          onClick={() => {
                            navigate('/orders');
                            closeAllDropdowns();
                          }}
                        >
                          عرض جميع الطلبات
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Enhanced Cart */}
            <div className="relative" ref={cartRef}>
              <button 
                onClick={toggleCart}
                className="p-2.5 hover:bg-gray-100 rounded-lg transition-all duration-200 relative hover:scale-105"
                aria-label="سلة المشتريات"
              >
                <ShoppingCart className="w-5 h-5 text-gray-600" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-bounce">
                    {cartItemCount}
                  </span>
                )}
              </button>

              {isCartOpen && (
                <div
                  className="
                    fixed inset-x-0 top-16 mx-2 z-50 max-w-sm w-full rounded-xl shadow-xl border border-gray-200 bg-white scrollbar-hide
                    md:absolute md:inset-x-auto md:top-full md:left-0 md:mt-2 md:w-80
                  "
                  style={{ maxHeight: '70vh', overflowY: 'auto' }}
                >               
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">سلة المشتريات ({cartItemCount} كتب)</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto scrollbar-hide">
                    {cartItems.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="text-gray-500">سلة المشتريات فارغة</p>
                      </div>
                    ) : (
                      cartItems.map((item) => {
                        const bookId = item.book_id;
                        
                        return (
                          <div key={bookId} className="p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <div className="flex gap-3">
                              {/* Book Image */}
                              <Link 
                                to={`/books/${bookId}`}
                                className="w-12 h-16 flex-shrink-0"
                                onClick={closeAllDropdowns}
                              >
                                <img 
                                  src={getImageUrl(item.image)}
                                  alt={item.title}
                                  className="w-full h-full object-cover rounded shadow-sm"
                                  onError={handleImageError()}
                                />
                              </Link>
                              
                              {/* Book Details */}
                              <div className="flex-1">
                                <Link 
                                  to={`/books/${bookId}`}
                                  className="block group"
                                  onClick={closeAllDropdowns}
                                >
                                  <h4 className="text-sm font-medium text-gray-900 mb-1 group-hover:text-orange-500 transition-colors">{item.title}</h4>
                                </Link>
                                
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm text-orange-600 font-medium">
                                    {Math.floor(item.price).toLocaleString()} ليرة
                                  </p>
                                </div>
                                
                                {/* Quantity Controls */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <button 
                                      onClick={() => handleUpdateQuantity(bookId, item.quantity, false)}
                                      disabled={updatingItem === bookId}
                                      className="w-6 h-6 rounded flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                    >
                                      -
                                    </button>
                                    
                                    <span className="text-xs font-medium w-4 text-center">
                                      {updatingItem === bookId ? (
                                        <Loader2 className="w-3 h-3 animate-spin mx-auto" />
                                      ) : (
                                        item.quantity
                                      )}
                                    </span>
                                    
                                    <button 
                                      onClick={() => handleUpdateQuantity(bookId, item.quantity, true)}
                                      disabled={updatingItem === bookId}
                                      className="w-6 h-6 rounded flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                    >
                                      +
                                    </button>
                                  </div>
                                  
                                  <button
                                    onClick={() => handleRemoveItem(bookId)}
                                    disabled={removingItem === bookId}
                                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                  >
                                    {removingItem === bookId ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-3 h-3" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="p-4 border-t border-gray-100">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-semibold text-gray-900">المجموع:</span>
                      <span className="font-bold text-orange-600 text-lg">
                        {(cartTotal || 0).toLocaleString()} ليرة
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Link 
                        to="/cart"
                        className="block w-full text-center border border-orange-500 text-orange-500 px-4 py-2 rounded-lg font-medium hover:bg-orange-50 transition-colors"
                        onClick={closeAllDropdowns}
                      >
                        عرض السلة
                      </Link>
                      <Link
                        to="/checkout"
                        className="block w-full text-center bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition-colors"
                        onClick={closeAllDropdowns}
                      >
                        إتمام الشراء
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* User Authentication Section */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button 
                  onClick={toggleUserMenu}
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-105"
                  aria-label="القائمة الشخصية"
                >
                  <User className="w-5 h-5 text-gray-600" />
                </button>

                {isUserMenuOpen && !loading && (
                  <div 
                    className="
                      fixed inset-x-0 top-16 mx-2 z-50 max-w-sm w-full rounded-xl shadow-xl border border-gray-200 bg-white
                      md:absolute md:inset-x-auto md:top-full md:left-0 md:mt-2 md:w-56
                    "
                    style={{ maxHeight: '70vh', overflowY: 'auto' }}
                  >
                    <div className="p-4 border-b border-gray-100">
                      <p className="font-semibold text-gray-900">{userProfile?.name}</p>
                      <p className="text-sm text-gray-500 truncate">{userProfile?.email}</p>
                    </div>
                    <div className="py-2 max-h-64 overflow-y-auto scrollbar-hide">
                      <Link 
                        to="/profile" 
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={closeAllDropdowns}
                      >
                        <User className="w-4 h-4 ml-2" />
                        الملف الشخصي
                      </Link>
                      <Link 
                        to="/orders" 
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={closeAllDropdowns}
                      >
                        <Package className="w-4 h-4 ml-2" />
                        طلباتي
                      </Link>
                      <Link 
                        to="/wishlist" 
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={closeAllDropdowns}
                      >
                        <Heart className="w-4 h-4 ml-2" />
                        المفضلة
                      </Link>
                      {isAdmin && (
                        <Link 
                          to="/admin" 
                          className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={closeAllDropdowns}
                        >
                          <Settings className="w-4 h-4 ml-2" />
                          لوحة التحكم
                        </Link>
                      )}
                    </div>
                    <div className="py-2 border-t border-gray-100">
                      <button 
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4 ml-2" />
                        تسجيل الخروج
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Login/Register buttons for non-authenticated users (Desktop only)
              <div className="hidden lg:flex items-center gap-2">
                <Link 
                  to="/login" 
                  className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors sm:px-4"
                  onClick={closeAllDropdowns}
                >
                  <span className="hidden sm:inline">تسجيل الدخول</span>
                  <span className="sm:hidden">دخول</span>
                </Link>
                <Link 
                  to="/register" 
                  className="px-3 py-2 text-sm font-medium bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 transition-colors sm:px-4"
                  onClick={closeAllDropdowns}
                >
                  <span className="hidden sm:inline">إنشاء حساب</span>
                  <span className="sm:hidden">حساب</span>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button 
              onClick={toggleMenu}
              className="lg:hidden p-2.5 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-105"
              aria-label="القائمة"
            >
              {isMenuOpen ? (
                <X className="w-5 h-5 text-gray-600" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Enhanced Mobile Menu */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMenuOpen ? 'max-h-screen opacity-100 pb-4' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="pt-4 border-t border-gray-100">
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActiveLink(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                      isActive 
                        ? 'bg-gradient-to-r from-orange-50 to-amber-50 text-orange-600' 
                        : 'text-gray-700 hover:bg-gray-50 hover:text-orange-500'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
              
              {/* Mobile Authentication Links - Only show when not authenticated */}
              {!isAuthenticated && (
                <div className="pt-2 border-t border-gray-100 mt-2">
                  <Link 
                    to="/login" 
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-500 font-medium transition-colors rounded-lg hover:bg-gray-50"
                    onClick={closeAllDropdowns}
                  >
                    <User className="w-5 h-5" />
                    تسجيل الدخول
                  </Link>
                  <Link 
                    to="/register" 
                    className="flex items-center gap-3 px-4 py-3 text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 font-medium transition-colors rounded-lg"
                    onClick={closeAllDropdowns}
                  >
                    <UserCircle className="w-5 h-5" />
                    إنشاء حساب جديد
                  </Link>
                </div>
              )}
              
              {isAdmin && (
                <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-500 font-medium transition-colors rounded-lg hover:bg-gray-50">
                  <Settings className="w-5 h-5" />
                  لوحة التحكم
                </Link>
              )}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;